//#region Imports
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { Dictionary } from "lodash";
import { z } from "zod";
//#endregion

export type MarketplaceSearchRequestJSON = z.infer<typeof MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ>;

//#region MarketplaceSearchRequestSchema
export default class MarketplaceSearchRequestSchema {
  public static readonly MarketplaceSearchRequestZ = z.object({
    query: z.string().optional(),
    hideCompanies: z.boolean().optional(),
    hideTeams: z.boolean().optional(),
    hideIndividuals: z.boolean().optional(),
    category: z.string().optional(),
    sortBy: z.string().optional(),
    name: z.string().optional(),
    yearEstablished: z.number().nullish(),
    numberOfEmployees: z.number().nullish(),
    contactPersonUserId: z.uuid().optional(),
    description: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    website: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    school: z.string().optional(),
    professionalRegistration: z.string().optional(),
    technicalBackground: z.string().optional(),
    applicationsDrafted: z.number().nullish(),
    allowedApplications: z.number().nullish(),
    barAdmissionYear: z.number().nullish(),
    isOnCall: z.boolean().optional(),
    insuredAmount: z.number().nullish(),
    billingRate: z.string().optional(),
    potentialDiversityCandidate: z.boolean().optional(),
    collaborates: z.boolean().optional(),
    additionalInfo: z.string().optional(),
    fluentLanguages: z.array(z.string()).optional(),
    technicalLanguages: z.array(z.string()).optional(),
    regionsRegistered: z.array(z.string()).optional(),
    regionsLicensed: z.array(z.string()).optional(),
    limitToVendorReps: z.boolean().optional(),
    from: z.number().default(0),
    size: z.number().default(10),
  });

  public static validateJSON(json: unknown): MarketplaceSearchRequestJSON {
    return parseOrThrow(MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ, json, {
      logError: true,
      schemaName: "MarketplaceSearchRequestSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is MarketplaceSearchRequestJSON {
    return MarketplaceSearchRequestSchema.MarketplaceSearchRequestZ.safeParse(json).success;
  }

  public static deserialize(filters?: Dictionary<string>): MarketplaceSearchRequestJSON {
    if (!filters) {
      return MarketplaceSearchRequestSchema.validateJSON({});
    }

    const parseInt = (value: string | undefined): number | null =>
      value ? Number.parseInt(value) : null;

    const parseBool = (value: string | undefined): boolean | undefined =>
      value !== undefined ? value === "true" : undefined;

    const parseArray = (value: string | undefined): string[] | undefined =>
      value ? value.split(",") : undefined;

    //#region Validation
    return MarketplaceSearchRequestSchema.validateJSON({
      query: filters["query"],
      category: filters["category"],
      hideCompanies: parseBool(filters["hideCompanies"]),
      hideTeams: parseBool(filters["hideTeams"]),
      hideIndividuals: parseBool(filters["hideIndividuals"]),
      sortBy: filters["sortBy"],
      name: filters["name"],
      yearEstablished: parseInt(filters["yearEstablished"]),
      numberOfEmployees: parseInt(filters["numberOfEmployees"]),
      contactPersonUserId: filters["contactPersonUserId"],
      description: filters["description"],
      address1: filters["address1"],
      address2: filters["address2"],
      city: filters["city"],
      state: filters["state"],
      postalCode: filters["postalCode"],
      website: filters["website"],
      firstName: filters["firstName"],
      lastName: filters["lastName"],
      school: filters["school"],
      professionalRegistration: filters["professionalRegistration"],
      technicalBackground: filters["technicalBackground"],
      applicationsDrafted: parseInt(filters["applicationsDrafted"]),
      allowedApplications: parseInt(filters["allowedApplications"]),
      barAdmissionYear: parseInt(filters["barAdmissionYear"]),
      isOnCall: parseBool(filters["isOnCall"]),
      insuredAmount: parseInt(filters["insuredAmount"]),
      billingRate: filters["billingRate"],
      potentialDiversityCandidate: parseBool(filters["potentialDiversityCandidate"]),
      collaborates: parseBool(filters["collaborates"]),
      additionalInfo: filters["additionalInfo"],
      fluentLanguages: parseArray(filters["fluentLanguages"]),
      technicalLanguages: parseArray(filters["technicalLanguages"]),
      regionsRegistered: parseArray(filters["regionsRegistered"]),
      regionsLicensed: parseArray(filters["regionsLicensed"]),
      limitToVendorReps: parseBool(filters["limitToVendorReps"]),
    });
  }

  //#endregion

  //#endregion

  //#region URLSearchParams

  public static toSearchParams(request: MarketplaceSearchRequestJSON): URLSearchParams {
    const params = new URLSearchParams();
    // this used to be a massive number of if statements, so just checkthe type and value instead.
    Object.entries(request).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (typeof value === 'boolean') {
        if (value) params.append(key, 'true');
      } else if (Array.isArray(value)) {
        if (value.length > 0) params.append(key, value.join(','));
      } else if (typeof value === 'string') {
        if (value) params.append(key, value);
      } else {
        // number
        params.append(key, value.toString());
      }
    });
    return params;
  }

  //#endregion
}
